import React from "react";

const DRAG_THRESHOLD = 6;

interface DragState {
  active: boolean;
  moved: boolean;
  startX: number;
  startScrollLeft: number;
  pointerId: number;
}

const initialState: DragState = {
  active: false,
  moved: false,
  startX: 0,
  startScrollLeft: 0,
  pointerId: 0,
};

const useDragScroll = (enabled: boolean) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const stateRef = React.useRef<DragState>({ ...initialState });
  const frameRef = React.useRef(0);
  const pendingScrollLeft = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);

  const flushScrollLeft = React.useCallback(() => {
    if (!frameRef.current) return;

    window.cancelAnimationFrame(frameRef.current);
    frameRef.current = 0;

    const element = ref.current;
    if (element) element.scrollLeft = pendingScrollLeft.current;
  }, []);

  React.useEffect(
    () => () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    },
    []
  );

  const endDrag = React.useCallback(() => {
    const state = stateRef.current;

    if (!state.active) return;

    flushScrollLeft();

    const element = ref.current;

    if (state.moved && element?.hasPointerCapture?.(state.pointerId)) {
      element.releasePointerCapture(state.pointerId);
    }

    state.active = false;
    setIsDragging(false);
  }, [flushScrollLeft]);

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const element = ref.current;

      if (!enabled || !element) return;
      if (event.pointerType === "touch" || event.pointerType === "pen") return;
      if (event.button > 0) return;

      stateRef.current = {
        active: true,
        moved: false,
        startX: event.clientX,
        startScrollLeft: element.scrollLeft,
        pointerId: event.pointerId,
      };
    },
    [enabled]
  );

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const element = ref.current;
      const state = stateRef.current;

      if (!enabled || !state.active || !element) return;

      const distance = event.clientX - state.startX;

      if (!state.moved) {
        if (Math.abs(distance) < DRAG_THRESHOLD) return;

        state.moved = true;
        setIsDragging(true);
        element.setPointerCapture?.(state.pointerId);
      }

      pendingScrollLeft.current = state.startScrollLeft - distance;

      if (frameRef.current) return;

      if (typeof window.requestAnimationFrame !== "function") {
        element.scrollLeft = pendingScrollLeft.current;
        return;
      }

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = 0;

        const target = ref.current;
        if (target) target.scrollLeft = pendingScrollLeft.current;
      });
    },
    [enabled]
  );

  const onClickCapture = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!stateRef.current.moved) return;

      stateRef.current.moved = false;
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const dragProps = React.useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onPointerLeave: endDrag,
      onClickCapture,
    }),
    [endDrag, onClickCapture, onPointerDown, onPointerMove]
  );

  return { ref, isDragging, dragProps };
};

export default useDragScroll;
